/**
 * Negócios — o funil (spec 02 §4 · spec 03 §3 · telas 3.5–3.7).
 *
 * Quatro ausências valem mais que os campos presentes:
 *
 * - **Não existe `probabilidade` no negócio.** Ela é do ESTÁGIO (DS-13), e chega como
 *   leitura junto do estágio. Copiá-la para o negócio congelaria o número no dia do
 *   cadastro, e mudar a probabilidade de um estágio deixaria de mover o ponderado.
 * - **Não existe `setor`.** É derivado do Cliente (`tipo_empresa.setor` — D-32), e
 *   chega só na saída. O formulário nunca o envia.
 * - **Não existe `valor_liquido` nem "valor faturado".** Fatura-se sempre o valor do
 *   pedido (D-75): o tratamento diz como a comissão é acertada, não quanto se fatura.
 *   "Líquido para a DV" é leitura gerencial da ficha, calculada lá, nunca gravada.
 * - **Não existe `higiene`.** É derivada — sem `proximaAcaoEm` OU categoria "Não
 *   definido" (spec 02 §4.3). Uma coluna guardada envelheceria sozinha.
 */
import { z } from 'zod'
import { COMPORTAMENTOS_DE_VENDA } from '@dv/domain'
import { fracaoDeComissao, codigoDeMoeda, dataNominal, limiteDoSeletor, listaDeUuidsNaUrl, uuid } from './comum.js'
import { referenciaDeUsuario } from './usuarios.js'
import { gatilhoDeComissao } from './cadastro.js'
import { parteReferenciada } from './referencias.js'

/**
 * O limite de parcelas, o MESMO no negócio, no pedido e na tela (D-185, DS-635). O negócio aceitava
 * 36 e o pedido 12: um negócio com 13 parcelas não virava pedido.
 */
export const MAXIMO_DE_PARCELAS = 12
const MENSAGEM_DO_LIMITE_DE_PARCELAS = `Até ${String(MAXIMO_DE_PARCELAS)} parcelas.`

// ---------------------------------------------------------------- vocabulário

/** D-40: "Não definido" é o valor inicial e NEUTRO — fora dos cenários de forecast. */
export const categoriaDePrevisao = z.enum(['Não definido', 'Garantido', 'Provável', 'Possível'])
export type CategoriaDePrevisao = z.infer<typeof categoriaDePrevisao>

/** D-36: os três tratamentos do campo "Como a comissão é acertada?". */
export const tratamentoDeComissao = z.enum(['Valor bruto', 'Valor líquido', 'Somente registro'])
export type TratamentoDeComissao = z.infer<typeof tratamentoDeComissao>

/** D-47: papel é da VENDA. Estes três, e só estes. */
export const papelDeParte = z.enum(['Cliente', 'Anunciante', 'Parceiro comissionado'])
export type PapelDeParte = z.infer<typeof papelDeParte>

export const formaDePagamento = z.enum(['unico', 'parcelado'])
/** D-41: "Intervalo (meses)" está extinto — o que existe é frequência, e Trimestral existe. */
export const frequenciaDeParcela = z.enum([
  'Mensal', 'Bimestral', 'Trimestral', 'Semestral', 'Anual', 'Customizado',
])
export type FrequenciaDeParcela = z.infer<typeof frequenciaDeParcela>

export const desfecho = z.enum(['aberto', 'ganho', 'perdido'])

// -------------------------------------------------------------------- saída

export const parteDoNegocio = z.object({
  id: uuid,
  papel: papelDeParte,
  empresaId: uuid.nullable(),
  contatoId: uuid.nullable(),
  /** O nome de quem quer que seja a parte — empresa ou pessoa. A tela não escolhe entre dois campos. */
  nome: z.string(),
  /** Só do Cliente/Anunciante: derivado do tipo da empresa (D-32). Nunca gravado. */
  setor: z.enum(['Público', 'Privado']).nullable(),
  percentual: z.string().nullable(),
  valorComissao: z.string().nullable(),
  tratamentoOverride: tratamentoDeComissao.nullable(),
  tipoComissao: z.enum(['Padrão', 'BV/Cashback']).nullable(),
  /** Quando a comissão nasce (PK-10, D-174): pré-preenchido da Capacidade e herdado pelo Pedido gerado. */
  gatilhoOverride: gatilhoDeComissao.nullable().default(null),
})
export type ParteDoNegocio = z.infer<typeof parteDoNegocio>

export const itemDoNegocio = z.object({
  id: uuid,
  produtoId: uuid,
  produtoNome: z.string(),
  subprodutoId: uuid.nullable(),
  subprodutoNome: z.string().nullable(),
  /**
   * O que está sendo comprado NESTE item — PÓS-V1-014.
   *
   * Contextual ao negócio, e não ao catálogo: renomear o sub-produto em Configurações
   * mudaria o nome para todos os negócios, que é o oposto do pedido. Também não é a
   * observação do negócio inteiro — dois sub-produtos têm descrições diferentes.
   */
  descricao: z.string().nullish(),
  /** NOME da forma efetiva do catálogo, só exibição (D-146); ausência em resposta anterior não presume assinatura. */
  formaVenda: z.string().nullish(),
  /** O COMPORTAMENTO da forma efetiva, que decide a mensalidade (D-146). */
  comportamentoVenda: z.enum(COMPORTAMENTOS_DE_VENDA).nullish(),
  valor: z.string().nullable(),
  moeda: codigoDeMoeda,
  ordem: z.number().int(),
})
export type ItemDoNegocio = z.infer<typeof itemDoNegocio>

export const parcelaPrevista = z.object({
  numero: z.number().int(),
  vencimento: dataNominal,
  valor: z.string().nullable(),
  /** D-41: a data editada à mão sobrevive à regeração — e é esta marca que a protege. */
  editadaManualmente: z.boolean(),
})
export type ParcelaPrevista = z.infer<typeof parcelaPrevista>

export const transicaoDeEstagio = z.object({
  deEstagioNome: z.string().nullable(),
  paraEstagioNome: z.string(),
  quando: z.string(),
  /** AB056: campo aditivo UTC; quando conserva o contrato legado DS-171. */
  quandoInstante: z.iso.datetime().optional(),
  porNome: z.string().nullable(),
})
export type TransicaoDeEstagio = z.infer<typeof transicaoDeEstagio>

/** A linha da lista 3.5 e do cartão do kanban — o que cabe sem abrir a ficha. */
export const linhaDeNegocio = z.object({
  id: uuid,
  codigo: z.string(),
  titulo: z.string(),
  estagioId: uuid,
  estagioNome: z.string(),
  /** Do CATÁLOGO (DS-13), nunca do negócio. Fração: 0,55 = 55%. */
  probabilidade: z.string(),
  desfecho,
  categoriaPrevisao: categoriaDePrevisao,
  valor: z.string().nullable(),
  moeda: codigoDeMoeda,
  /** D-74: Cliente é o nome principal; Anunciante entre parênteses quando informado e diferente. */
  clienteNome: z.string().nullable(),
  anuncianteNome: z.string().nullable(),
  /**
   * A empresa de cada nome acima, para o nome abrir a ficha dela (D-136, DS-538). Sai da
   * MESMA junção que dá o nome: nulo quando o nome é nulo, e nulo quando a empresa foi
   * excluída, porque a ficha dela responderia 404 e link para 404 é link morto.
   */
  clienteEmpresaId: uuid.nullable(),
  anuncianteEmpresaId: uuid.nullable(),
  /** Derivado do Cliente (D-32) — o negócio não tem setor próprio. */
  setor: z.enum(['Público', 'Privado']).nullable(),
  pracaNome: z.string().nullable(),
  responsavelNome: z.string().nullable(),
  /**
   * Os parceiros comissionados, 0..N, por extenso — a lista mostra o primeiro e "+N".
   *
   * Cada um é a PARTE referenciada (a empresa, ou o contato quando é pessoa física), e não
   * só o nome (D-136, DS-538): a forma antiga, uma lista de nomes, foi SUBSTITUÍDA, e não
   * duplicada. Quem precisa só do texto lê `.nome`.
   */
  parceiros: z.array(parteReferenciada),
  /**
   * O que está sendo VENDIDO, um rótulo por item distinto: o nome do produto, com o
   * sub-produto junto SÓ quando ele tem forma de venda própria (spec 02 §3.3). É o que
   * separa "PageSignal Assinatura" de "PageSignal Setup" sem fixar nome de produto no
   * código — PageSignal é UM produto (D-24), e quem os distingue é o catálogo.
   */
  produtos: z.array(z.string()),
  /**
   * Algum item é assinatura — comportamento `assinatura` da forma de venda resolvida
   * `COALESCE(subproduto, produto)` (D-146), nunca o nome da forma.
   *
   * Identifica a presença de assinatura; não anualiza o total de um negócio misto. É DERIVADA e não
   * gravada, pela mesma razão da probabilidade: mudar a forma de venda de um sub-produto
   * no catálogo tem de mudar o que os cartões dizem, não só o que os próximos gravarem.
   */
  recorrente: z.boolean(),
  /** DS493: mensalidades somadas; null quando ausentes/ocultas. Campos aditivos para clientes anteriores. */
  valorMensal: z.string().nullish(),
  /** Itens sem recorrência, separados da mensalidade. Nunca incluídos no equivalente anual. */
  valorPontual: z.string().nullish(),
  previsaoFechamento: dataNominal.nullable(),
  proximaAcao: z.string().nullable(),
  proximaAcaoEm: dataNominal.nullable(),
  ganhoEm: dataNominal.nullable(),
  perdidoEm: dataNominal.nullable(),
})
export type LinhaDeNegocio = z.infer<typeof linhaDeNegocio>

export const negocio = linhaDeNegocio.extend({
  entityId: uuid,
  responsavelId: uuid.nullable(),
  pracaId: uuid.nullable(),
  origemId: uuid.nullable(),
  origemNome: z.string().nullable(),
  formaPagamento: formaDePagamento,
  numParcelas: z.number().int().nullable(),
  frequencia: frequenciaDeParcela.nullable(),
  tratamentoComissao: tratamentoDeComissao.nullable(),
  perfilFaturamentoId: uuid.nullable(),
  perfilFaturamentoRotulo: z.string().nullable(),
  /** O prazo EFETIVO do Cliente, de `fn_prazo_empresa`. A ficha nunca diz "15–45D". */
  clientePrazoDias: z.number().int().nullable(),
  motivoPerdaId: uuid.nullable(),
  motivoPerdaNome: z.string().nullable(),
  motivoPerdaDetalhe: z.string().nullable(),
  proximaAcaoResponsavelId: uuid.nullable(),
  /**
   * O NOME de quem responde pela pendência — a ficha mostra gente, não identificador.
   *
   * Com só o id, a 3.6 teria de baixar a lista inteira de usuários atribuíveis para
   * escrever uma linha de texto. Nulo quando ninguém foi nomeado; nesse caso a ficha
   * lê o responsável do negócio, que é de quem a pendência é por padrão.
   */
  proximaAcaoResponsavelNome: z.string().nullable(),
  /** Um negócio ganho gera no máximo um PV; a ficha usa o vínculo, não uma busca por código. */
  pedidoId: uuid.nullable(),
  pedidoCodigo: z.string().nullable(),
  partes: z.array(parteDoNegocio),
  itens: z.array(itemDoNegocio),
  parcelas: z.array(parcelaPrevista),
  historico: z.array(transicaoDeEstagio),
})
export type Negocio = z.infer<typeof negocio>

/** Spec3.7: leitura autorizada para escrever preserva os campos exigidos pelo formulário. */
export const negocioParaEdicao = negocio.extend({
  valor: z.string(),
  itens: z.array(itemDoNegocio.extend({ valor: z.string() })),
  parcelas: z.array(parcelaPrevista.extend({ valor: z.string() })),
})
export type NegocioParaEdicao = z.infer<typeof negocioParaEdicao>

/** Uma coluna do quadro: contagem e soma vêm do SERVIDOR, não de somar os cartões da página. */
export const colunaDoKanban = z.object({
  chave: z.string(),
  rotulo: z.string(),
  ordem: z.number().int(),
  probabilidade: z.string().nullable(),
  quantidade: z.number().int(),
  soma: z.string().nullable(),
})
export type ColunaDoKanban = z.infer<typeof colunaDoKanban>

export const resumoDoFunil = z.object({
  colunas: z.array(colunaDoKanban),
  /** Σ do valor dos abertos no recorte. */
  pipelineAberto: z.string().nullable(),
  /** Σ valor × probabilidade DO ESTÁGIO (DS-13). Nunca entra no gap de meta. */
  ponderado: z.string().nullable(),
  abertos: z.number().int(),
  ganhos: z.object({ quantidade: z.number().int(), soma: z.string().nullable() }),
  perdidos: z.object({ quantidade: z.number().int(), soma: z.string().nullable() }),
  /** Sem próxima ação OU categoria "Não definido" (spec 02 §4.3) — derivada, sempre. */
  higiene: z.number().int(),
})
export type ResumoDoFunil = z.infer<typeof resumoDoFunil>

/** Um parceiro elegível: PJ (empresa) ou PF (contato), com os defaults da capacidade. */
export const parceiroElegivel = z.object({
  empresaId: uuid.nullable(),
  contatoId: uuid.nullable(),
  nome: z.string(),
  tipoParceiroNome: z.string(),
  tipoComissaoDefault: z.enum(['Padrão', 'BV/Cashback']),
  percentualDefault: z.string().nullable(),
  valorFixoDefault: z.string().nullable(),
  /**
   * PK-10 (D-129): a Capacidade é a fonte ÚNICA da taxa e também de quando a comissão nasce (D-174) — o
   * Negócio e o Pedido pré-preenchem daqui, sem sobrescrever o que já foi digitado.
   */
  gatilhoDefault: gatilhoDeComissao.default('recebimento'),
  /** A moeda do valor fixo da Capacidade: um fixo em outra moeda não pré-preenche a venda. */
  moedaDefault: z.string().nullable().default(null),
})
export type ParceiroElegivel = z.infer<typeof parceiroElegivel>

/**
 * A busca dos parceiros elegíveis pelo seletor (item 03 · DS-529).
 *
 * `q` ignora acento em nome, nome fantasia e nome do contato; `chaves` são os ids que o
 * pedido e o negócio gravam (empresa OU contato), e é por eles que o seletor resolve o
 * nome de um valor que não está na página. Sem `limit`, a lista inteira, como antes.
 */
export const buscaDeParceiros = z.object({
  q: z.string().max(120).optional(),
  limit: limiteDoSeletor.optional(),
  chaves: listaDeUuidsNaUrl.optional(),
})
export type BuscaDeParceiros = z.infer<typeof buscaDeParceiros>

/** `data` é o de sempre; `meta.total` conta o recorte inteiro, para a tela dizer "20 de 57". */
export const respostaDeParceirosElegiveis = z.object({
  data: z.array(parceiroElegivel),
  meta: z.object({ total: z.number().int() }),
})
export type RespostaDeParceirosElegiveis = z.infer<typeof respostaDeParceirosElegiveis>

/** A pessoa do seletor de responsável e de menção — com a foto (PK-04). */
export const usuarioAtribuivel = referenciaDeUsuario
export type UsuarioAtribuivel = z.infer<typeof usuarioAtribuivel>

// ------------------------------------------------------------------- entrada

/**
 * O corpo de `POST`/`PATCH /negocios`.
 *
 * `codigo` não está aqui: quem o gera é a sequência (DS-42), e a tela nunca o inventa.
 * `estagioId` também não é obrigatório na criação — o default é o estágio aberto de
 * menor ordem, e resolvê-lo no servidor evita que a tela decida qual é o "primeiro".
 */
export const entradaDeNegocio = z.object({
  titulo: z.string().trim().min(1, 'Informe o título do negócio.'),
  responsavelId: uuid.nullish(),
  estagioId: uuid.nullish(),
  categoriaPrevisao: categoriaDePrevisao.default('Não definido'),
  valor: z.string().nullish(),
  moeda: codigoDeMoeda.nullish(),
  pracaId: uuid.nullish(),
  origemId: uuid.nullish(),
  previsaoFechamento: dataNominal.nullish(),
  formaPagamento: formaDePagamento.default('unico'),
  numParcelas: z.number().int().min(1).max(MAXIMO_DE_PARCELAS, MENSAGEM_DO_LIMITE_DE_PARCELAS).nullish(),
  frequencia: frequenciaDeParcela.nullish(),
  tratamentoComissao: tratamentoDeComissao.nullish(),
  perfilFaturamentoId: uuid.nullish(),
  proximaAcao: z.string().nullish(),
  proximaAcaoEm: dataNominal.nullish(),
  proximaAcaoResponsavelId: uuid.nullish(),
})
export type EntradaDeNegocio = z.input<typeof entradaDeNegocio>

/**
 * A edição — **sem os `.default()`**, e isso não é estilo.
 *
 * `.partial()` torna a chave opcional e NÃO desliga o `.default()`: parsear `{}`
 * devolveria `categoriaPrevisao: 'Não definido'` e `formaPagamento: 'unico'`. Num
 * `PATCH` que grava tudo o que não veio `undefined`, isso significa que **classificar
 * um negócio e depois editar a próxima ação o devolveria a "Não definido"** — e
 * "Não definido" tira o negócio dos cenários de forecast (D-40). O teste de higiene
 * do 4.2 pegou isso, e a mesma forma existia no `PATCH /empresas/:id`, reescrevendo
 * o país para BR a cada edição.
 *
 * Default é para o que NASCE. Edição só grava o que veio.
 */
export const entradaDeEdicaoDeNegocio = entradaDeNegocio
  .omit({ categoriaPrevisao: true, formaPagamento: true })
  .extend({ categoriaPrevisao: categoriaDePrevisao, formaPagamento: formaDePagamento })
  .partial()
export type EntradaDeEdicaoDeNegocio = z.input<typeof entradaDeEdicaoDeNegocio>

/**
 * Uma parte, como o `PUT /negocios/:id/partes` a recebe.
 *
 * Empresa OU contato: parceiro pode ser pessoa física (D-47), Cliente e Anunciante são
 * sempre empresa. A validação cruzada (1 Cliente, Anunciante ≠ Cliente, D-70) é do
 * serviço, porque depende do conjunto — um Zod por linha não vê o array inteiro.
 */
export const entradaDeParte = z.object({
  papel: papelDeParte,
  empresaId: uuid.nullish(),
  contatoId: uuid.nullish(),
  percentual: fracaoDeComissao.nullish(),
  valorComissao: z.string().nullish(),
  tratamentoOverride: tratamentoDeComissao.nullish(),
  tipoComissao: z.enum(['Padrão', 'BV/Cashback']).nullish(),
  gatilhoOverride: gatilhoDeComissao.nullish(),
})
export type EntradaDeParte = z.input<typeof entradaDeParte>

export const entradaDeItem = z.object({
  produtoId: uuid,
  subprodutoId: uuid.nullish(),
  /** PÓS-V1-014. Opcional: negócio antigo e item sem detalhe seguem válidos. */
  descricao: z.string().trim().max(500, 'A descrição do item tem no máximo 500 caracteres.').nullish(),
  valor: z.string(),
  moeda: codigoDeMoeda.nullish(),
})
export type EntradaDeItem = z.input<typeof entradaDeItem>

export const entradaDeParcela = z.object({
  numero: z.number().int().min(1),
  vencimento: dataNominal,
  valor: z.string(),
  editadaManualmente: z.boolean().default(false),
})
export type EntradaDeParcela = z.input<typeof entradaDeParcela>

/** A agenda prevista do negócio inteira, no limite do pedido (D-185). */
export const entradaDeParcelasDoNegocio = z.array(entradaDeParcela).max(MAXIMO_DE_PARCELAS, MENSAGEM_DO_LIMITE_DE_PARCELAS)

export const entradaDeMoverEstagio = z.object({ estagioId: uuid })

/**
 * Ganhar pede a DATA, e só (D-88).
 *
 * Havia um `valorFinal` aqui, e ele criava dois números para a mesma venda: o dos itens
 * e o do fechamento. O valor do negócio É o somatório dos itens — desconto se dá
 * ajustando os itens, e é lá que ele fica registrado no produto que o concedeu.
 */
export const entradaDeGanhar = z.object({
  data: dataNominal,
})

/** DS-34: perder exige motivo do catálogo. Perdido sem motivo é o que não volta depois. */
export const entradaDePerder = z.object({
  motivoPerdaId: uuid,
  detalhe: z.string().nullish(),
})

export type EntradaDeMoverEstagio = z.input<typeof entradaDeMoverEstagio>
export type EntradaDeGanhar = z.input<typeof entradaDeGanhar>
export type EntradaDePerder = z.input<typeof entradaDePerder>
